import type { FormEventHandler, FormHTMLAttributes, Ref } from 'react';
import { createElement, forwardRef } from 'react';

export interface FormProps extends FormHTMLAttributes<HTMLFormElement> {
  /** Enable or disable the form submit event */
  enableSubmit?: boolean;
  innerRef?: Ref<HTMLFormElement>;
}

/**
 * A form HTML Element to prevent and stop the propagation of the submit events.
 *
 * @see https://corgi-x.tfd.engineering/components/form and https://corgi-x.tfd.engineering/guides/form
 */
export function Form({
  children,
  onSubmit,
  enableSubmit = true,
  innerRef,
  ...props
}: FormProps) {
  const handleSubmit: FormEventHandler<HTMLFormElement> = e => {
    e.preventDefault();
    e.stopPropagation();

    if (!enableSubmit) {
      return;
    }
    onSubmit?.(e);
  };

  return (
    <form
      data-name="Form"
      noValidate
      onSubmit={handleSubmit}
      ref={innerRef}
      {...props}
    >
      {children}
    </form>
  );
}

export default forwardRef(
  (props: FormProps, innerRef: Ref<HTMLFormElement>) => {
    const WrappedForm = createElement(Form, { ...props, innerRef });
    return WrappedForm;
  }
);
